import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productsAPI } from '../../services/api';
import { Save, X, Plus, Trash2 } from 'lucide-react';
import Toast from '../../components/Toast';
import ImageUpload from '../../components/ImageUpload';

const ProductForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        shortDesc: '',
        fullDesc: '',
        category: 'Powder',
        featured: false,
        image: '/logo.png',
        npkN: 0,
        npkP: 0,
        npkK: 0,
        micros: [],
        sizes: [{ weight: '1 kg', price: 0 }],
    });

    useEffect(() => {
        if (isEdit) {
            fetchProduct();
        }
    }, [id]);

    const fetchProduct = async () => {
        try {
            setLoading(true);
            const response = await productsAPI.getById(id);
            // Handle both old format (response.data.data) and new format (response.data)
            const product = response.data?.id ? response.data : response.data?.data;

            // Handle both formats: npk object or npkN/npkP/npkK fields
            const npkN = product.npk?.n ?? product.npkN ?? 0;
            const npkP = product.npk?.p ?? product.npkP ?? 0;
            const npkK = product.npk?.k ?? product.npkK ?? 0;

            setFormData({
                name: product.name,
                slug: product.slug,
                shortDesc: product.shortDesc,
                fullDesc: product.fullDesc,
                category: product.category,
                featured: product.featured,
                image: product.image,
                npkN: npkN,
                npkP: npkP,
                npkK: npkK,
                micros: product.micros || [],
                sizes: product.sizes || [{ weight: '1 kg', price: 0 }],
            });
        } catch (error) {
            console.error('Error fetching product:', error);
            showToast('Failed to load product', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: parseInt(value) || 0,
        });
    };

    const handleSizeChange = (index, field, value) => {
        const newSizes = [...formData.sizes];
        newSizes[index] = {
            ...newSizes[index],
            [field]: field === 'price' ? parseFloat(value) || 0 : value,
        };
        setFormData({ ...formData, sizes: newSizes });
    };

    const addSize = () => {
        setFormData({
            ...formData,
            sizes: [...formData.sizes, { weight: '', price: 0 }],
        });
    };

    const removeSize = (index) => {
        setFormData({
            ...formData,
            sizes: formData.sizes.filter((_, i) => i !== index),
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (isEdit) {
                await productsAPI.update(id, formData);
                showToast('Product updated successfully', 'success');
            } else {
                await productsAPI.create(formData);
                showToast('Product created successfully', 'success');
            }
            setTimeout(() => navigate('/admin/products'), 1500);
        } catch (error) {
            console.error('Error saving product:', error);
            showToast(error.response?.data?.message || 'Failed to save product', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type) => {
        setToast({ message, type });
    };

    const categories = ['Powder', 'Suspension', 'Chelated Micronutrients', 'Specialized Fertilizers', 'Liquid', 'Flakes'];

    if (loading && isEdit) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-earth-green"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900">
                    {isEdit ? 'Edit Product' : 'Add New Product'}
                </h1>
                <p className="text-gray-600 mt-1">
                    {isEdit ? 'Update product information' : 'Create a new product in your catalog'}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Product Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Slug *
                            </label>
                            <input
                                type="text"
                                name="slug"
                                value={formData.slug}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Category *
                            </label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            >
                                {categories.map((cat) => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        <div className="md:col-span-2">
                            <ImageUpload
                                value={formData.image}
                                onChange={(url) => setFormData({ ...formData, image: url })}
                                label="Product Image"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Short Description *
                            </label>
                            <input
                                type="text"
                                name="shortDesc"
                                value={formData.shortDesc}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Full Description *
                            </label>
                            <textarea
                                name="fullDesc"
                                value={formData.fullDesc}
                                onChange={handleChange}
                                required
                                rows={4}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                name="featured"
                                checked={formData.featured}
                                onChange={handleChange}
                                className="h-4 w-4 text-earth-green focus:ring-earth-green border-gray-300 rounded"
                            />
                            <label className="ml-2 block text-sm text-gray-700">
                                Featured Product
                            </label>
                        </div>
                    </div>
                </div>

                {/* NPK Values */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">NPK Values</h2>
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Nitrogen (N)
                            </label>
                            <input
                                type="number"
                                name="npkN"
                                value={formData.npkN}
                                onChange={handleNumberChange}
                                min="0"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Phosphorus (P)
                            </label>
                            <input
                                type="number"
                                name="npkP"
                                value={formData.npkP}
                                onChange={handleNumberChange}
                                min="0"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Potassium (K)
                            </label>
                            <input
                                type="number"
                                name="npkK"
                                value={formData.npkK}
                                onChange={handleNumberChange}
                                min="0"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                            />
                        </div>
                    </div>
                </div>

                {/* Sizes & Pricing */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold text-gray-900">Sizes & Pricing</h2>
                        <button
                            type="button"
                            onClick={addSize}
                            className="btn-secondary inline-flex items-center space-x-1 text-sm"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Add Size</span>
                        </button>
                    </div>
                    <div className="space-y-3">
                        {formData.sizes.map((size, index) => (
                            <div key={index} className="flex gap-3">
                                <input
                                    type="text"
                                    placeholder="Weight (e.g., 1 kg)"
                                    value={size.weight}
                                    onChange={(e) => handleSizeChange(index, 'weight', e.target.value)}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                                />
                                <input
                                    type="number"
                                    placeholder="Price"
                                    value={size.price}
                                    onChange={(e) => handleSizeChange(index, 'price', e.target.value)}
                                    min="0"
                                    step="0.01"
                                    className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                                />
                                {formData.sizes.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeSize(index)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                    >
                                        <Trash2 className="h-5 w-5" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={() => navigate('/admin/products')}
                        className="btn-outline inline-flex items-center space-x-2"
                    >
                        <X className="h-5 w-5" />
                        <span>Cancel</span>
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
                    >
                        <Save className="h-5 w-5" />
                        <span>{loading ? 'Saving...' : isEdit ? 'Update Product' : 'Create Product'}</span>
                    </button>
                </div>
            </form>

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default ProductForm;
